import { Link } from "wouter";
import { ArrowRight, ChevronDown } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import AnimatedSection from "@/components/AnimatedSection";
import PageHero from "@/components/PageHero";
import { IMAGES } from "@/lib/constants";
import RelatedArticles from "@/components/RelatedArticles";

export default function Svetsning() {
  return (
    <Layout>
      <SEOHead
        title="Svetsning i Eskilstuna – stål, rostfritt och aluminium | ASPAB"
        description="Svetsning i stål, rostfritt och aluminium som del av en helhetslösning med laserskärning och kantpressning. Begär offert från ASPAB."
        canonical="https://aspab.com/tjanster/svetsning"
        path="/tjanster/svetsning"
        breadcrumbs={[
          { name: "Tjänster", href: "/tjanster" },
          { name: "Svetsning", href: "/tjanster/svetsning" },
        ]}
              ogImage={IMAGES.ogSvetsning}
      />

      <PageHero
        title="Svetsning i Eskilstuna"
        subtitle="Kvalificerad svetsning som komplement till laserskärning och kantpressning. Vi levererar färdiga komponenter, från skärning till ytbehandling."
        breadcrumbs={[
          { name: "Tjänster", href: "/tjanster" },
          { name: "Svetsning", href: "/tjanster/svetsning" },
        ]}
        image={IMAGES.workshop2}
      />

      <section className="bg-white py-20 md:py-28">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-14">
            <AnimatedSection>
              <div>
                <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">Komplett tillverkning</span>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                  Från plåt till färdig produkt
                </h2>
                <p className="text-gray-500 mt-4 text-lg leading-relaxed">
                  Svetsning är en naturlig del av vår helhetslösning inom metallbearbetning.
                  Genom att erbjuda svetsning tillsammans med laserskärning och kantpressning
                  kan vi leverera färdiga komponenter utan att du behöver koordinera flera
                  underleverantörer.
                </p>
                <p className="text-gray-500 mt-4 leading-relaxed">
                  Vi hanterar hela kedjan: skuret, bockat, svetsat och ytbehandlat. Oavsett om
                  det gäller prototyper eller serieproduktion ser vi till att varje steg håller
                  samma höga kvalitet.
                </p>
                <p className="text-gray-500 mt-4 leading-relaxed">
                  Med vår konsultativa approach hjälper vi dig att optimera konstruktionen för
                  bästa svetsbarhet och hållbarhet. Vi har erfarenhet av att svetsa allt från
                  tunna plåtar till tjockare konstruktionsstål.
                </p>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={200}>
              <div className="space-y-6">
                {[
                  { title: "Helhetslösning", desc: "Skärning, bockning, svetsning och ytbehandling under samma tak. En kontaktperson, en leverantör, snabbare leverans." },
                  { title: "Materialkunskap", desc: "Vi har erfarenhet av att svetsa i svart stål, rostfritt, aluminium och höghållfasthetsstål. Rätt metod för rätt material." },
                  { title: "Kvalitetskontroll", desc: "Samma höga kvalitetskrav som i vår laserskärning. Varje svets kontrolleras för att säkerställa hållbarhet och passform." },
                  { title: "Ytbehandling", desc: "Vi erbjuder egen pulverlackering och har etablerade samarbetspartners för andra ytbehandlingar." },
                ].map((item) => (
                  <div key={item.title} className="bg-white border border-gray-200 rounded-lg p-5 hover:border-[#2563EB]/20 transition-colors">
                    <h3 className="font-heading text-lg font-semibold text-gray-900">{item.title}</h3>
                    <p className="text-gray-500 text-sm mt-2 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </AnimatedSection>
          </div>

          <AnimatedSection delay={100}>
            <div className="mt-16 bg-gray-50 border border-gray-200 rounded-lg p-8 md:p-12">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <h3 className="font-heading text-2xl font-bold text-gray-900">
                    Behöver du en komplett lösning?
                  </h3>
                  <p className="text-gray-500 mt-3 leading-relaxed">
                    Vi tar hand om hela processen, från ritning till färdig produkt.
                    Kontakta oss för att diskutera ditt projekt.
                  </p>
                </div>
                <div className="md:text-right">
                  <Link
                    href="/kontakt"
                    className="inline-flex items-center gap-2 px-8 py-4 bg-[#2563EB] text-white font-semibold rounded-lg hover:bg-[#3B82F6] transition-colors"
                  >
                    Begär offert <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-white py-16 md:py-20 border-t border-gray-200">
        <div className="container">
          <AnimatedSection>
            <div className="max-w-3xl mx-auto">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-8">
                Vanliga frågor om svetsning
              </h2>
              <div className="space-y-4">
                {[
                  { q: "Vilka material kan ni svetsa?", a: "Vi svetsar i svart stål, rostfritt stål, aluminium och höghållfasthetsstål. Vi väljer rätt svetsmetod baserat på materialtyp och projektets krav." },
                  { q: "Kan ni hantera hela processen från skärning till svetsning?", a: "Ja, det är en av våra största fördelar. Vi erbjuder laserskärning, kantpressning, svetsning och ytbehandling under samma tak, vilket ger kortare ledtider och bättre kvalitetskontroll." },
                  { q: "Erbjuder ni även ytbehandling?", a: "Ja, vi har egen kapacitet för pulverlackering och samarbetar med partners för andra ytbehandlingar som varmförzinkning och blästring." },
                  { q: "Hanterar ni både prototyper och serier?", a: "Absolut. Vi hanterar allt från enstaka prototyper till större serier. Vår flexibla maskinpark och erfarna personal gör det möjligt att anpassa oss efter varje projekts behov." },
                  { q: "Hur säkerställer ni kvaliteten på svetsningen?", a: "Varje svets kontrolleras för att säkerställa hållbarhet och passform. Vi tillämpar samma höga kvalitetskrav som i vår laserskärning och kantpressning." },
                ].map((faq, i) => (
                  <details key={i} className="group border border-gray-200 rounded-lg">
                    <summary className="flex items-center justify-between cursor-pointer p-5 text-gray-900 font-medium hover:bg-gray-50 transition-colors">
                      {faq.q}
                      <ChevronDown className="w-5 h-5 text-gray-500 group-open:rotate-180 transition-transform" />
                    </summary>
                    <div className="px-5 pb-5 text-gray-600 leading-relaxed">
                      {faq.a}
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Kunskapsbank */}
      <RelatedArticles
        heading="Lär dig mer om svetsning och metallbearbetning"
        subheading="Fördjupa dig i svetsmetoder, material och kvalitetskontroll med våra expertartiklar."
        articles={[
          { title: "Svetsmetoder för industriell plåt", slug: "svetsning-metoder-industri", excerpt: "MIG, TIG och MAG – vilken svetsmetod passar bäst för ditt projekt?" },
          { title: "Kvalitetskontroll inom metallbearbetning", slug: "svetsning-kvalitetskontroll", excerpt: "Från första bit-kontroll till slutinspektion – så säkerställer vi kvalitet." },
          { title: "Ytbehandling av laserskurna detaljer", slug: "ytbehandling-plat", excerpt: "Gradning, blästring och lackering – så efterbehandlar du plåtdetaljer." },
          { title: "Korrosionsskydd för stålkonstruktioner", slug: "korrosionsskydd-metall", excerpt: "Varmförzinkning, pulverlackering och andra metoder för långsiktigt skydd." },
          { title: "Materialguide: stål, rostfritt, aluminium", slug: "materialguide-stal-rostfritt-aluminium", excerpt: "Egenskaper och val av rätt material för svetsning och konstruktion." },
          { title: "Prototyp till serie", slug: "laserskarning-prototyper", excerpt: "Så tar du en idé från prototyp till serieproduktion med rätt partner." },
        ]}
      />
    </Layout>
  );
}
